import * as CryptoJS from 'crypto-js';

export class EncryptionService {
  // Use a more secure key in production - this should be stored in environment variables
  private static readonly SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'SAGRF_DEFAULT_ENCRYPTION_KEY_2024';

  /**
   * Encrypts sensitive data using AES encryption
   * @param data - The data to encrypt
   * @returns The encrypted data as a string
   */
  static encrypt(data: string): string {
    if (!data || data.trim() === '') {
      return '';
    }
    
    try {
      const encrypted = CryptoJS.AES.encrypt(data, this.SECRET_KEY).toString();
      return encrypted;
    } catch (error) {
      console.error('Error encrypting data:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypts sensitive data using AES decryption
   * @param encryptedData - The encrypted data to decrypt
   * @returns The decrypted data as a string
   */
  static decrypt(encryptedData: string): string {
    if (!encryptedData || encryptedData.trim() === '') {
      return '';
    }
    
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.SECRET_KEY);
      const originalData = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!originalData) {
        throw new Error('Failed to decrypt data - invalid key or corrupted data');
      }
      
      return originalData;
    } catch (error) {
      // Silently fail - this may be unencrypted legacy data
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Encrypts an ID number specifically
   * @param idNumber - The ID number to encrypt
   * @returns The encrypted ID number
   */
  static encryptIdNumber(idNumber: string): string {
    return this.encrypt(idNumber);
  }

  /**
   * Decrypts an ID number specifically
   * Uses safe decryption to handle legacy unencrypted data
   * @param encryptedIdNumber - The encrypted ID number to decrypt
   * @returns The decrypted ID number or original if decryption fails
   */
  static decryptIdNumber(encryptedIdNumber: string): string {
    return this.safeDecrypt(encryptedIdNumber);
  }

  /**
   * Validates if a string appears to be encrypted (basic check)
   * @param data - The data to check
   * @returns True if the data appears to be encrypted
   */
  static isEncrypted(data: string): boolean {
    if (!data || data.trim() === '') {
      return false;
    }
    
    // CryptoJS encrypted strings start with "U2FsdGVkX1" (Base64 for "Salted__")
    if (data.startsWith('U2FsdGVkX1')) {
      return true;
    }
    
    // Basic check for Base64-like encrypted string
    // AES encrypted strings are typically Base64 encoded
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    return base64Regex.test(data) && data.length > 20; // Encrypted strings are typically longer
  }
  
  /**
   * Validates if a string looks like a valid South African ID number (13 digits)
   * @param idNumber - The ID number to validate
   * @returns True if it looks like a valid ID number
   */
  static isValidIdNumberFormat(idNumber: string): boolean {
    if (!idNumber || idNumber.trim() === '') {
      return false;
    }
    // South African ID numbers are 13 digits
    return /^\d{13}$/.test(idNumber.trim());
  }

  /**
   * Safely decrypt data, returning the original data if decryption fails
   * This handles legacy unencrypted data gracefully
   * @param encryptedData - The data to decrypt
   * @returns The decrypted data or the original data if decryption fails
   */
  static safeDecrypt(encryptedData: string): string {
    if (!encryptedData || encryptedData.trim() === '') {
      return '';
    }
    
    try {
      const decrypted = this.decrypt(encryptedData);
      // Verify decryption worked by checking if result is different and valid
      if (decrypted && decrypted !== encryptedData) {
        return decrypted;
      }
      // If decryption returned same value or empty, it likely failed
      return encryptedData;
    } catch (error) {
      // If decryption fails, assume it's legacy unencrypted data
      // and return it as-is
      console.warn('safeDecrypt failed, returning original:', error);
      return encryptedData;
    }
  }
  
  /**
   * Test if encryption/decryption is working properly
   * Useful for debugging encryption issues
   */
  static testEncryption(testData: string = '1234567890123'): boolean {
    try {
      const encrypted = this.encrypt(testData);
      const decrypted = this.decrypt(encrypted);
      return decrypted === testData;
    } catch (error) {
      console.error('Encryption test failed:', error);
      return false;
    }
  }
}
